import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import toast from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import slug from 'slug';

import { EMPTY_FUNCTION, EMPTY_OBJECT } from '../../lib/globals';
import { formatErrorToString, getTestProps } from '../../lib/helpers';

import Button from '../../components/Button/Button';
import Tooltip from '../../components/Tooltip/Tooltip';

import useDebounceCallback from '../../hooks/useDebounceCallback';
import useSelectedSpace from '../../hooks/useSelectedSpace';

import ContentTypeFormContext from '../../contexts/ContentTypeFormContext';
import {
  defaultButtonsProps,
  defaultTextRender,
  useModals,
} from '../../contexts/ModalContext';

import { CopyIcon, QuestionMarkCircleIcon } from '../../images/shapes';

import { useAppForm } from '../../form-hooks/form';
import PropertyModalForm from '../PropertyModalForm/PropertyModalForm';
import { EDITABLE } from '../PropertyModalForm/helpers/propertiesFields';
import DraggableCard from './DraggableCard/DraggableCard';
import {
  changeOrderDown,
  changeOrderUp,
  deleteProperty,
  updateOrder,
  updateProperties,
} from './helpers/parser';
import { removeNulls } from './helpers/submit-transform';
import { generateZodSchema } from './helpers/zod';

const parsePropErrors = (
  errors,
  newPropertiesErrors,
  key,
  findRegexp,
  group,
) => {
  const propsErrors = Object.keys(errors).filter((errorKey) =>
    errorKey.includes(key),
  );
  if (propsErrors.length) {
    propsErrors.forEach((errorKey) => {
      const { propName, prop } = errorKey.match(findRegexp).groups;
      if (!prop) {
        newPropertiesErrors[propName] = {
          ...newPropertiesErrors[propName],
          global: [
            ...(newPropertiesErrors[propName]?.global || []),
            errors[errorKey],
          ],
        };
      } else {
        newPropertiesErrors[propName] = {
          ...newPropertiesErrors[propName],
          form: {
            ...newPropertiesErrors[propName]?.form,
            ...(prop === 'required'
              ? { [prop]: errors[errorKey] }
              : { [`${group}.${prop}`]: errors[errorKey] }),
          },
        };
      }
    });
  }
};

const ContentTypeForm = ({
  contentType = EMPTY_OBJECT,
  isEditing = false,
  onSubmit,
  disabled = false,
  navigateOnSave = EMPTY_OBJECT,
  onMediaUpload = EMPTY_FUNCTION,
  testId = '',
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const modal = useModals();
  const { buildUrlWithSpace } = useSelectedSpace();

  const metaPath = 'metaDefinition';
  const schemaPath = 'schemaDefinition.allOf[1]';
  const requiredBasePath = 'schemaDefinition';

  const [fieldsToDelete, setFieldsToDelete] = useState([]);
  const oldOrder = useMemo(
    () => contentType?.metaDefinition?.order || [],
    [contentType],
  );
  const [propertiesErrors, setPropertiesErrors] = useState({});
  const [transformError, setTransformError] = useState();
  const [transformCoError, setTransformCoError] = useState();
  const [dirty, setDirty] = useState(false);

  const validationSchema = useMemo(() => generateZodSchema(t), [t]);

  const onFormSubmit = useCallback(
    async ({ value, formApi }) => {
      if (fieldsToDelete.length) {
        const result = await modal({
          title: defaultTextRender.warning(t('Global.Warning')),
          content: (
            <Trans i18nKey="ContentTypeForm.ConfirmDeleteProperties">
              <div className="font-bold inline">
                {{ properties: fieldsToDelete.join(', ') }}
              </div>
            </Trans>
          ),
          buttons: [
            {
              ...defaultButtonsProps.save,
              label: t('Global.Save'),
              result: true,
            },
            {
              ...defaultButtonsProps.cancel,
              label: t('Global.Cancel'),
              result: false,
            },
          ],
        });
        if (!result) return;
      }

      setDirty(false);
      removeNulls(value);
      const [, errors, hasErrors] = await onSubmit(value);

      if (!hasErrors) {
        setFieldsToDelete([]);
        setPropertiesErrors({});
        setTransformCoError();
        setTransformError();

        if (navigateOnSave.current) {
          navigate(buildUrlWithSpace(`content-type-definitions`));
        }
      } else {
        setDirty(true);
        if (!errors || !Object.keys(errors).length) return;
        const newPropertiesErrors = {};
        parsePropErrors(
          errors,
          newPropertiesErrors,
          'metaDefinition.propertiesConfig',
          /metaDefinition\.propertiesConfig[.[](?<propName>[^.\]]*)]?(?:\.(?<prop>[^.]+))?/,
          'config',
        );
        parsePropErrors(
          errors,
          newPropertiesErrors,
          'schemaDefinition.allOf[1].properties',
          /schemaDefinition\.allOf\[1\]\.properties[.[](?<propName>[^.\]]*)]?(?:\.(?<prop>[^.]+))?/,
          'schema',
        );
        setPropertiesErrors(newPropertiesErrors);

        setTransformCoError(errors.co);
        setTransformError(errors.ctd || errors['']);

        Object.entries(errors)
          .filter(([key]) => ['label', 'name', 'draftPublic'].includes(key))
          .forEach(([key, error]) => {
            formApi.setFieldMeta(key, (prev) => ({
              ...prev,
              errorMap: { onSubmit: error },
            }));
          });
      }
    },
    [
      buildUrlWithSpace,
      fieldsToDelete,
      modal,
      navigate,
      navigateOnSave,
      onSubmit,
      t,
    ],
  );

  const initialValues = useMemo(
    () =>
      contentType
        ? JSON.parse(JSON.stringify(contentType))
        : {
            label: '',
            name: '',
            draftPublic: false,
            metaDefinition: { order: [], propertiesConfig: {} },
            schemaDefinition: {
              additionalProperties: false,
              required: [],
              type: 'object',
              allOf: [
                {
                  $ref: '#/components/schemas/AbstractContentTypeSchemaDefinition',
                },
                { type: 'object', properties: {} },
              ],
            },
          },
    [contentType],
  );

  const form = useAppForm({
    defaultValues: initialValues,
    onSubmit: onFormSubmit,
    validators: { onChange: validationSchema, onBlur: validationSchema },
  });

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    },
    [form],
  );

  const labelFieldListeners = useMemo(
    () => ({
      onBlur: ({ value, fieldApi }) => {
        if (fieldApi.form.getFieldValue('name')) return;
        fieldApi.form.setFieldValue(
          'name',
          slug(value, {
            replacement: '_',
            charmap: { '/': '_' },
          }).replace(/\d/g, ''),
        );
        fieldApi.form.validateField('name');
      },
    }),
    [],
  );

  const apiKeyFieldValidators = useMemo(
    () => ({
      onChange: ({ value }) => {
        if (!value) return t('Form.FormErrorNotBlank');
        if (!/^[a-zA-Z_]*$/.test(value)) {
          return t('ContentTypeForm.Errors.ApiKey');
        }
      },
    }),
    [t],
  );

  const orderFieldValidators = useMemo(
    () => ({
      onChange: ({ value }) => {
        if (!value?.length) return t('ContentTypeForm.Errors.OneProperty');
      },
    }),
    [t],
  );

  const handleDeleteField = useCallback(
    async (fieldName) => {
      let result = null;
      const showModal = isEditing && oldOrder.indexOf(fieldName) > -1;
      if (!showModal) result = true;
      else result = await modal.delete(t('ContentTypeForm.DeleteProperty'));
      if (result) {
        if (showModal) setFieldsToDelete((prev) => [...prev, fieldName]);

        deleteProperty(
          form,
          fieldName,
          metaPath,
          schemaPath,
          requiredBasePath,
          setDirty,
        );
      }
    },
    [isEditing, oldOrder, modal, t, form],
  );

  const handleChangeOrder = useCallback(
    (sourceName, targetName) => {
      updateOrder(sourceName, targetName, form, metaPath, setDirty);
    },
    [form],
  );

  const onDropCallback = useDebounceCallback(handleChangeOrder, 10);

  const handleOrderUp = useCallback(
    (idx) => {
      changeOrderUp(form, metaPath, idx, setDirty);
    },
    [form],
  );

  const handleOrderDown = useCallback(
    (idx) => {
      changeOrderDown(form, metaPath, idx, setDirty);
    },
    [form],
  );

  const updateFormikProperties = useCallback(
    (property, newProperty, copiedPropertyIdx) => {
      updateProperties(
        form,
        property,
        newProperty,
        copiedPropertyIdx,
        metaPath,
        schemaPath,
        requiredBasePath,
        setDirty,
      );
    },
    [form],
  );

  const ctdContextValue = useMemo(
    () => ({
      oldCtd: contentType,
      setDirty,
      disabled,
      isEditing,
      onMediaUpload,
      propertiesErrors,
    }),
    [contentType, isEditing, onMediaUpload, propertiesErrors, disabled],
  );

  const openPropertyModal = useCallback(
    async (
      fieldName,
      fieldProps,
      fieldSchema,
      isRequired,
      copiedPropertyIdx,
    ) => {
      let property = null;
      if (fieldName) {
        property = { key: fieldName };
        property.config = fieldProps || { label: '' };
        property.schema = fieldSchema || { type: '' };
        property.required = !!isRequired;
      }

      const newProperty = await modal({
        title: (
          <div className="font-bold text-xl sm:text-3xl">
            {t('ContentTypeForm.ContentProperty')}
          </div>
        ),
        content: (
          <ContentTypeFormContext value={ctdContextValue}>
            <PropertyModalForm
              property={property}
              order={form.getFieldValue('metaDefinition.order')}
              isNew={
                copiedPropertyIdx > -1 || oldOrder.indexOf(property?.key) < 0
              }
              isDuplicate={copiedPropertyIdx > -1}
              {...getTestProps(testId, 'property-form', 'testId')}
            />
          </ContentTypeFormContext>
        ),
        size: '3xl',
        dialogAdditionalClasses: 'h-full lg:max-h-[calc(100vh-2rem)]',
      });

      if (!newProperty) return;

      updateFormikProperties(
        copiedPropertyIdx > -1 ? null : property,
        newProperty,
        copiedPropertyIdx,
      );

      form.validateField('metaDefinition.order');
    },
    [modal, t, ctdContextValue, form, oldOrder, testId, updateFormikProperties],
  );

  const handleEditField = useCallback(
    async (
      fieldName,
      fieldProps,
      fieldSchema,
      isRequired,
      copiedPropertyIdx = -1,
    ) => {
      if (EDITABLE.indexOf(fieldProps.inputType) < 0) {
        alert('Not implemented');
        return;
      }
      await openPropertyModal(
        fieldName,
        fieldProps,
        fieldSchema,
        isRequired,
        copiedPropertyIdx,
      );
    },
    [openPropertyModal],
  );

  const renderField = useCallback(
    (fieldName, idx, currentOrder, values) => (
      <DraggableCard
        key={fieldName}
        form={form}
        fieldName={fieldName}
        idx={idx}
        currentOrder={currentOrder}
        fieldProps={values.metaDefinition?.propertiesConfig?.[fieldName]}
        fieldSchema={
          values.schemaDefinition?.allOf?.[1]?.properties?.[fieldName]
        }
        isRequired={values.schemaDefinition?.required.indexOf(fieldName) > -1}
        onDelete={handleDeleteField}
        onEdit={handleEditField}
        onDrop={onDropCallback}
        onUp={handleOrderUp}
        onDown={handleOrderDown}
        onDuplicate={handleEditField}
        testId={testId}
      />
    ),
    [
      form,
      handleDeleteField,
      handleEditField,
      onDropCallback,
      handleOrderUp,
      handleOrderDown,
      testId,
    ],
  );

  const onCopy = useCallback(() => {
    toast.success(t('ContentTypeForm.CopiedDefinition'));
  }, [t]);

  return (
    <ContentTypeFormContext value={ctdContextValue}>
      <form.AppForm>
        <form
          id="ctd-form"
          className="space-y-2 md:space-y-4 p-5 md:py-10 md:px-12"
          onSubmit={handleSubmit}
          noValidate
        >
          <div className="flex flex-col sm:flex-row gap-2 lg:gap-10">
            <form.AppField name="label" listeners={labelFieldListeners}>
              {(field) => (
                <field.Input
                  label={t('ContentDefinition.Label')}
                  disabled={disabled}
                  additionalClasses="max-w-2xl"
                  helpText={
                    <div className="flex">
                      <QuestionMarkCircleIcon className="text-blue w-3 ml-1  mr-1" />
                      {t('Form.FormCreateFolderHelpText')}
                    </div>
                  }
                  visualSeparateSlash={true}
                  required
                  {...getTestProps(testId, 'label', 'testId')}
                />
              )}
            </form.AppField>

            {/**
             * There are problems that main validation has no actual data of this field,
             * due to the fact that it is set programmatically.
             */}
            <form.AppField name="name" validators={apiKeyFieldValidators}>
              {(field) => (
                <field.Input
                  label={
                    <Tooltip
                      tooltip={t('ContentTypeForm.ApiNameTooltip')}
                      additionalClasses="w-fit inline-flex"
                      phoneTooltipPlacement={'rightCenter'}
                    >
                      {t('ContentTypeForm.Name')}
                      <QuestionMarkCircleIcon className="text-blue w-3 ml-1" />
                    </Tooltip>
                  }
                  additionalClasses="max-w-2xl"
                  disabled={isEditing || disabled}
                  required
                  {...getTestProps(testId, 'name', 'testId')}
                />
              )}
            </form.AppField>
          </div>

          {transformError && (
            <div
              className="text-red"
              {...getTestProps(testId, 'ctd-transform-errors')}
            >
              {formatErrorToString(transformError)}
            </div>
          )}
          {transformCoError && (
            <div
              className="flex flex-col text-red"
              {...getTestProps(testId, 'co-transform-errors')}
            >
              <span className="font-bold mb-2">
                {t('ContentDefinition.Transformation.Errors')}
              </span>
              <span>{t('ContentDefinition.Transformation.Description')}</span>
              <span className="whitespace-pre-line">
                {Object.keys(transformCoError)
                  .filter((key) => key)
                  .map((key) => `-${key}: ${transformCoError[key]} \n`)}
              </span>
            </div>
          )}

          <div className="space-y-2">
            <form.AppField name="draftPublic">
              {(field) => (
                <field.Switch
                  label={t('ContentTypeForm.DraftPublic')}
                  helpText={t('ContentTypeForm.DraftPublicHelpText')}
                  disabled={disabled}
                  additionalClasses="justify-items-start"
                  labelUp
                  {...getTestProps(testId, 'draftPublic', 'testId')}
                />
              )}
            </form.AppField>
          </div>

          <form.Subscribe selector={({ values }) => values}>
            {(values) =>
              (values.metaDefinition?.order || []).map(
                (fieldName, idx, currentOrder) =>
                  renderField(fieldName, idx, currentOrder, values),
              )
            }
          </form.Subscribe>

          {/**
           * There are problems that main validation has no actual data of this field,
           * due to the fact that it is set programmatically.
           */}
          <form.AppField
            name="metaDefinition.order"
            validators={orderFieldValidators}
          >
            {(subField) => (
              <subField.ErrorField
                {...getTestProps(testId, 'properties-error', 'testId')}
              />
            )}
          </form.AppField>

          <div className="flex justify-between items-center">
            <Button
              onClick={() => openPropertyModal()}
              buttonSize="sm"
              disabled={disabled}
              {...getTestProps(testId, 'add-property', 'testId')}
            >
              {t('ContentTypeForm.AddProperty')}
            </Button>

            <form.Subscribe selector={({ values }) => values}>
              {(values) => (
                <CopyToClipboard
                  text={JSON.stringify(values, null, 2)}
                  onCopy={onCopy}
                >
                  <CopyIcon
                    className={
                      'cursor-pointer h-5 w-5 hover:opacity-50 dark:text-gray-300'
                    }
                  />
                </CopyToClipboard>
              )}
            </form.Subscribe>
          </div>

          <form.DirtyHandler dirty={dirty} />
          <form.ValidationToastHandler />
        </form>
      </form.AppForm>
    </ContentTypeFormContext>
  );
};

export default ContentTypeForm;

ContentTypeForm.propTypes = {
  /**
   * On submit callback
   */
  onSubmit: PropTypes.func.isRequired,
  /**
   * Content type to edit
   */
  contentType: PropTypes.object,
  /**
   * If form is used for object editing
   */
  isEditing: PropTypes.bool,
  /**
   * If form is disabled
   */
  disabled: PropTypes.bool,
  /**
   * If navigates after submit represented by react ref
   */
  navigateOnSave: PropTypes.object,
  /**
   * Callback for media uploading
   */
  onMediaUpload: PropTypes.func,
  /**
   * Test id for page
   */
  testId: PropTypes.string,
};
